import { Box } from '@mui/material';
import chroma from 'chroma-js';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { fontSize, TypographyStyledGlobal, colors, fontWeight, quantilesColorScale } from '../../../theme';
import { CALCULATION_MODE } from '../../Modals/DashboardModal/constants';
import { Evolution } from '../dashboardInterfaces';
import { KeyFigureIndicatorFooter, KeyFigureIndicatorStyled, DividerStyled, ComputeIndicatorStyled } from '../styled';
import { EVOLUTION_ICONS } from './evolution';

interface KeyFigureIndicatorProps {
        keyFigureNumber?: string[] | number[] | undefined | (string | number)[],
        previousNumber?: string
        round?: number
        year?: string
        computeMethod?: string
        quantiles?: { key: string, value: number }
        comparedNumber?: number
        base: number
        comparisonType?: string
        evolution?: Evolution | null
}

const EVOLUTION_ICON_SIZE = 24;

const KeyFigureIndicator: FC<KeyFigureIndicatorProps> = ({ keyFigureNumber, previousNumber, round, year, computeMethod, quantiles, comparedNumber, base, comparisonType, evolution }) => {
    const { t } = useTranslation('dashboard');
    const EvolutionIcon = evolution ? EVOLUTION_ICONS[evolution] : null;
    let computeMethodLabel = t('percentage', { modality: computeMethod });
    const isNPSPositive = computeMethod === CALCULATION_MODE.NPS && keyFigureNumber && keyFigureNumber[0] > 0;
    const isAverage = computeMethod === CALCULATION_MODE.AVERAGE;
    const isNPS = computeMethod === CALCULATION_MODE.NPS;
    const plus = isNPSPositive ? '+' : '';
    const percent = !isNPSPositive && !isAverage && !isNPS ? '%' : '';
    const quantilesColorScaleToApply = (quantiles && Object.keys(quantiles).length > 0) ? chroma.scale(quantilesColorScale.shade1).mode('lch').colors(Object.keys(quantiles).length) : [];
    const kpiFontColorToApply: string = (() => {
        if (!quantilesColorScaleToApply || !quantiles) {
            return colors.grey100;
        }
        const quantileEntries = Object.entries(quantiles);

        // Trouve l'index de la première valeur <= keyFigureNumber
        const foundIndex = quantileEntries.findIndex(([, value]) => value <= Number(keyFigureNumber));

        // Si aucune valeur ne satisfait la condition, utilise la dernière couleur
        // Sinon, utilise la couleur correspondant à l'index trouvé
        return foundIndex === -1
            ? quantilesColorScaleToApply[quantilesColorScaleToApply.length - 1]
            : quantilesColorScaleToApply[foundIndex];
    })();

    if (isNPS) {
        computeMethodLabel = t('nps');
    } else if (isAverage) {
        computeMethodLabel = t('average');
    }

    return (
        <KeyFigureIndicatorStyled>
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <TypographyStyledGlobal
                        color={kpiFontColorToApply}
                        margin='0 0px 0 0'
                        size={fontSize.xxl}
                        weight={fontWeight.bold}
                    >
                        {plus + Number(keyFigureNumber).toFixed(round) + percent}
                    </TypographyStyledGlobal>
                    {EvolutionIcon && (
                        // The files carry their fill on each path, the theme color overrides it there.
                        <Box sx={{ display: 'flex', '& path': { fill: colors.grey200 } }}>
                            <EvolutionIcon height={EVOLUTION_ICON_SIZE} width={EVOLUTION_ICON_SIZE} />
                        </Box>
                    )}
                </Box>
                <ComputeIndicatorStyled>
                    {computeMethodLabel}
                </ComputeIndicatorStyled>
                {comparedNumber != null && base > 0 && (
                    <TypographyStyledGlobal
                        color={colors.grey400}
                        margin='10px 0 0 0'
                        size={fontSize.sm}
                        weight={fontWeight.semiRegular}
                    >
                        {`${comparisonType === 'TEMPORAL' ? t('compareToGlobal.temporalLegend') : t('compareToGlobal.legend')} : ${isNPS && comparedNumber > 0 ? '+' : ''}${Number(comparedNumber).toFixed(round)}${percent}`}
                    </TypographyStyledGlobal>
                )}
            </Box>
            {quantiles && Object.keys(quantiles).length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '18px', marginTop: '16px', justifyContent: 'center' }}>
                    {Object.entries(quantiles).map(([key, value], index) => (
                        <Box
                            key={key}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <Box
                                sx={{
                                    width: '35px',
                                    height: '13px',
                                    backgroundColor: quantilesColorScaleToApply[index],
                                    borderRadius: '2px',
                                }}
                            />
                            <TypographyStyledGlobal
                                color={colors.grey200}
                                size={fontSize.sm}
                                weight={fontWeight.regular}
                            >
                                {plus + Number(value).toFixed(round) + percent}
                            </TypographyStyledGlobal>
                        </Box>
                    ))}
                </Box>
            )}
            {previousNumber && (
                <>
                    <TypographyStyledGlobal
                        color={colors.grey400}
                        fontStyle='italic'
                        size={fontSize.xl}
                        weight={fontWeight.semiBold}
                    >
                        {previousNumber}
                    </TypographyStyledGlobal>
                    <KeyFigureIndicatorFooter>
                        <DividerStyled />
                        {t('waveReminder', { year })}
                    </KeyFigureIndicatorFooter>
                </>
            )}
        </KeyFigureIndicatorStyled>
    );
};

export default KeyFigureIndicator;
